import moment, { Moment } from 'moment';
import { DefaultDateFormats } from '../../constants/dateFormats';

export const enum PeriodType {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
  ALL = 'all',
}

export interface Period {
  readonly type: PeriodType;
  readonly startTime: Date | null;
  readonly endTime: Date | null;
}

export const dailyPeriod = (date: Date): Period => {
  return {
    type: PeriodType.DAY,
    startTime: date,
    endTime: date,
  };
};

export const weeklyPeriod = (startOfWeek: Date, endOfWeek: Date): Period => {
  return {
    type: PeriodType.WEEK,
    startTime: startOfWeek,
    endTime: endOfWeek,
  };
};

export const currentWeeklyPeriod = (): Period => {
  return {
    type: PeriodType.WEEK,
    startTime: moment()
      .startOf('week')
      .toDate(),
    endTime: moment()
      .endOf('week')
      .toDate(),
  };
};

export const monthlyPeriod = (year: number, month: number): Period => {
  return {
    type: PeriodType.MONTH,
    startTime: new Date(year, month - 1, 1),
    endTime: new Date(year, month, 0),
  };
};

export const yearlyPeriod = (year: number): Period => {
  return {
    type: PeriodType.YEAR,
    startTime: new Date(year, 0, 1),
    endTime: new Date(year, 12, 0, 23, 59, 59, 999),
  };
};

export const allRangePeriod = (): Period => {
  return {
    type: PeriodType.ALL,
    startTime: null,
    endTime: null,
  };
};

export const formatPeriod = (period: Period): string => {
  switch (period.type) {
    case PeriodType.DAY:
      return moment(period.startTime!).format('MMM D, YYYY');
    case PeriodType.WEEK:
      const startOfWeek = moment(period.startTime!).format('D');
      const endOfWeek = moment(period.endTime!).format('D MMM, YYYY');
      return `${startOfWeek} - ${endOfWeek}`;
    case PeriodType.MONTH:
      return moment(period.startTime!).format('MMMM YYYY');
    case PeriodType.YEAR:
      return moment(period.startTime!).format('YYYY');
    case PeriodType.ALL:
      return 'All time';
  }
};

export const moveToNextPeriod = (period: Period) => {
  const startTime = new Date(period.startTime || Date.now());
  const endTime = new Date(period.endTime || Date.now());

  switch (period.type) {
    case PeriodType.DAY:
      const date = moment(startTime)
        .add(1, 'd')
        .toDate();
      return dailyPeriod(date);
    case PeriodType.WEEK:
      const startOfWeek = moment(startTime)
        .add(1, 'w')
        .toDate();
      const endOfWeek = moment(endTime)
        .add(1, 'w')
        .toDate();
      return weeklyPeriod(startOfWeek, endOfWeek);
    case PeriodType.MONTH:
      const year = moment(startTime).year();
      const month = moment(startTime).month();
      return monthlyPeriod(year, month + 2);
    case PeriodType.YEAR:
      return yearlyPeriod(moment(startTime).year() + 1);
    case PeriodType.ALL:
      return period;
  }
};

export const moveToPrevPeriod = (period: Period) => {
  const startTime = new Date(period.startTime || Date.now());
  const endTime = new Date(period.endTime || Date.now());

  switch (period.type) {
    case PeriodType.DAY:
      const date = moment(startTime)
        .subtract(1, 'd')
        .toDate();
      return dailyPeriod(date);
    case PeriodType.WEEK:
      const startOfWeek = moment(startTime)
        .subtract(1, 'w')
        .toDate();
      const endOfWeek = moment(endTime)
        .subtract(1, 'w')
        .toDate();
      return weeklyPeriod(startOfWeek, endOfWeek);
    case PeriodType.MONTH:
      const year = moment(startTime).year();
      const month = moment(startTime).month();
      return monthlyPeriod(year, month);
    case PeriodType.YEAR:
      return yearlyPeriod(moment(startTime).year() - 1);
    case PeriodType.ALL:
      return period;
  }
};

export const convertPeriod = (period: Period, requiredType: PeriodType): Period => {
  if (period.type === requiredType) {
    return period;
  }

  const startTime = new Date(Date.now());
  const endTime = new Date(Date.now());
  switch (requiredType) {
    case PeriodType.DAY:
      const date = moment(startTime)
        .startOf('d')
        .toDate();
      return dailyPeriod(date);
    case PeriodType.WEEK:
      const startOfWeek = moment(startTime)
        .startOf('W')
        .toDate();
      const endOfWeek = moment(endTime)
        .endOf('W')
        .toDate();
      return weeklyPeriod(startOfWeek, endOfWeek);
    case PeriodType.MONTH:
      const year = moment(startTime).year();
      const month = moment(startTime).month();
      return monthlyPeriod(year, month + 1);
    case PeriodType.YEAR:
      return yearlyPeriod(moment(startTime).year());
    case PeriodType.ALL:
      return allRangePeriod();
  }
};

const buildPeriodRange = (period: Period): PeriodRange => {
  const startMoment = moment(period.startTime!);
  const endMoment = moment(period.endTime!);

  const days = endMoment.diff(startMoment, 'd') + 1;

  return {
    startMoment,
    endMoment,
    days,
  };
};

const dateIdFormatCache: any = {};

export const getPageId = (pageMoment: Moment) => {
  const unixTime = pageMoment.unix();
  let pageId = dateIdFormatCache[unixTime];

  if (!pageId) {
    pageId = pageMoment.format(DefaultDateFormats.fullDate);
    dateIdFormatCache[unixTime] = pageId;
  }

  return pageId;
};

export const enumerateDaysInPeriod = (period: Period, enumerator: (date: Date) => void): any => {
  const periodRange = buildPeriodRange(period);

  let datePosition = moment(periodRange.startMoment);
  for (let i = 0; i < periodRange.days; i++) {
    enumerator(datePosition.toDate());

    datePosition = datePosition.add(1, 'd');
  }
};

interface PeriodRange {
  readonly days: number;
  readonly startMoment: Moment;
  readonly endMoment: Moment;
}



// WEBPACK FOOTER //
// ./src/containers/Stats/Models.ts